import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import t from 'react-translate';

import { useAppDispatch } from 'redux/store';
import { openConfirmationDialog } from 'redux/actions/confirmation-dialogs';
import { getCurrentUser } from 'redux/selectors/users';

import { AngularContext } from 'react-app';

import ConversationModalContent from 'lecture_pages/components/ai-role-play/ai-conversation-modal';
import AiConversationContext, {
  AIRolePlayStatus,
  AudioRecordingStatus,
  Message,
  SessionData,
  VoiceRecordingState,
} from 'lecture_pages/components/ai-role-play/ai-conversation-modal/context';
import useAiConversationHandlers from 'lecture_pages/components/ai-role-play/hooks/use-ai-conversation-handlers';
import { RolePlayInteractionMode } from 'redux/schemas/models/ai-role-play';
import useAiRolePlaySubmissionPusherEvents from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play-submission-pusher-events';
import { RootState } from 'redux/schemas';
import useAiRolePlay from 'lecture_pages/components/ai-role-play/hooks/use-ai-role-play';
import useVoiceAiConversationHandlers from 'lecture_pages/components/ai-role-play/hooks/use-voice-ai-conversation-handlers';
import { getCourse } from 'redux/selectors/course';
import RolePlaySubmissionModalStyles from './role-play-submission-modal-styles';

const NewConversationView = () => {
  const dispatch = useAppDispatch();
  const { injectServices } = useContext(AngularContext);
  const [
    $state,
    PageLevelManager,
    StateManager,
  ] = injectServices(['$state', 'PageLevelManager', 'StateManager']);

  // The lecture page component runs useAiRolePlaySubmissionPusherEvents while mounted beneath
  // this modal. Skip the modal here when opened from a lecture page to avoid a double points
  // modal — but keep the hook enabled so setMessages still fires for voice transcripts.
  const lastParentState = PageLevelManager?.lastParent()?.state;
  const isLecturePageInBackground = !!(lastParentState?.$$state?.()?.includes?.['lecture-page-parent']);
  const rolePlayId = Number($state?.params?.rolePlayId);

  const aiRolePlay = useAiRolePlay(rolePlayId);

  const { courseCatalogId, courseId, interactionMode } = aiRolePlay ?? {};

  const course = useSelector((state: RootState) => getCourse(state, courseCatalogId));
  const { anonymizedIdentifier } = useSelector((state: RootState) => getCurrentUser(state));

  const [aiRolePlayStatus, setAiRolePlayStatus] = useState(AIRolePlayStatus.NOT_STARTED);
  const [showScenarioDetails, setShowScenarioDetails] = useState(true);
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTimeout, setIsTimeout] = useState(false);
  const [isRolePlayStarting, setIsRolePlayStarting] = useState(false);
  const [openingNarrative, setOpeningNarrative] = useState('');
  const [isAwaitingAiResponse, setIsAwaitingAiResponse] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [sessionData, setSessionData] = useState<SessionData>({
    aiApiEndpoint: null,
    jwtToken: null,
    submissionId: null,
    expiresAt: null,
    languageLocale: null,
  });
  const [audioRecordingStatus, setAudioRecordingStatus] = useState(AudioRecordingStatus.NOT_STARTED);
  const [microphoneEnabled, setMicrophoneEnabled] = useState(false);
  const [inactivityCount, setInactivityCount] = useState(0);

  const audioStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const [voiceRecordingState, setVoiceRecordingState] = useState<VoiceRecordingState | null>(VoiceRecordingState.IDLE);

  const isHardDeadlineExpired = !!(aiRolePlay?.hardDeadline && aiRolePlay?.expired);

  const {
    handleChunk,
    handleError,
    handleStartTextConversation,
  } = useAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    messages,
    setMessages,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    openingNarrative,
    setOpeningNarrative,
    setIsAwaitingAiResponse,
  });

  const resetConversation = useCallback(() => {
    // Abort any existing stream before resetting state
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    if (aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      setMessages([]);
    }
    setVoiceRecordingState(VoiceRecordingState.IDLE);
    setAiRolePlayStatus(AIRolePlayStatus.NOT_STARTED);
    setShowScenarioDetails(true);
    setSessionData({
      aiApiEndpoint: null,
      jwtToken: null,
      submissionId: null,
      expiresAt: null,
      languageLocale: null,
    });
    setIsTimeout(false);
    setIsAwaitingAiResponse(false);
    setAudioRecordingStatus(AudioRecordingStatus.NOT_STARTED);
    setMicrophoneEnabled(false);
    setInactivityCount(0);
  }, [aiRolePlayStatus, setVoiceRecordingState]);

  const {
    handleStartVoiceConversation,
    aiVoiceCardState,
    learnerVoiceCardState,
    aiMediaStream,
    learnerMediaStream,
    isStreamError,
    handleRestartFromStreamError,
  } = useVoiceAiConversationHandlers({
    aiRolePlayId: aiRolePlay?.id,
    setAiRolePlayStatus,
    setShowScenarioDetails,
    sessionData,
    setSessionData,
    setIsTimeout,
    abortControllerRef,
    setIsRolePlayStarting,
    setMicrophoneEnabled,
    audioStreamRef,
    recorderRef,
    setOpeningNarrative,
    setVoiceRecordingState,
    resetConversation,
    setInactivityCount,
    inactivityCount,
  });

  const handleStartConversation = useCallback(() => {
    if (interactionMode === RolePlayInteractionMode.VOICE) {
      handleStartVoiceConversation();
    } else {
      handleStartTextConversation();
    }
  }, [interactionMode, handleStartVoiceConversation, handleStartTextConversation]);

  useAiRolePlaySubmissionPusherEvents({
    anonymizedIdentifier,
    courseId: course?.id,
    aiRolePlayId: aiRolePlay?.id,
    courseCatalogId,
    pointsReceived: aiRolePlay?.pointsReceived,
    skillTags: [],
    skipModal: isLecturePageInBackground,
    interactionMode,
    setMessages,
  });

  /**
   * Exits the chat. Shows confirmation dialog if conversation is in progress,
   * unless skipConfirmation is true (used for timeout scenarios).
   */
  const handleExitChat = useCallback((skipConfirmation = false) => {
    if (!skipConfirmation && aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS) {
      dispatch(openConfirmationDialog({
        title: t.FORM.ARE_YOU_SURE(),
        bodyText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE(),
        confirmText: t.LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.CONFIRM(),
        cancelText: t.FORM.CANCEL(),
        onConfirm: resetConversation,
      }));
    } else {
      resetConversation();
    }
  }, [aiRolePlayStatus, dispatch, resetConversation]);

  // The modal's "X" close button bypasses handleExitChat entirely (plain $state.go),
  // so abort on unmount is the only guaranteed way to stop the stream/audio.
  useEffect(() => () => {
    abortControllerRef.current?.abort();
  }, []);

  // StateManager intercepts that same "X" button (and browser back/tab close) to show
  // the exit warning before the transition proceeds.
  const checkUnsavedChanges = useCallback(
    () => aiRolePlayStatus === AIRolePlayStatus.IN_PROGRESS,
    [aiRolePlayStatus],
  );

  useEffect(() => StateManager.registerStateChangeStart(
    checkUnsavedChanges,
    'shared/templates/exit-ai-role-play-confirmation.html',
    'LECTURE_PAGES.COMPONENTS.AI_ROLE_PLAY.CONVERSATION_MODAL.EXIT_CHAT_CONFIRMATION.MESSAGE',
  ), [checkUnsavedChanges, StateManager]);

  const wrapperStyles = css`
    height: 100%;
    > div {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
  `;

  return (
    <>
      <RolePlaySubmissionModalStyles />
      <AiConversationContext.Provider
        value={{
          interactionMode,
          handleExitChat,
          aiRolePlayStatus,
          setAiRolePlayStatus,
          showScenarioDetails,
          setShowScenarioDetails,
          aiRolePlayId: aiRolePlay.id,
          messages,
          setMessages,
          sessionData,
          setSessionData,
          isTimeout,
          setIsTimeout,
          abortControllerRef,
          isHardDeadlineExpired,
          isRolePlayStarting,
          setIsRolePlayStarting,
          openingNarrative,
          handleChunk,
          handleError,
          handleStartConversation,
          isAwaitingAiResponse,
          audioRecordingStatus,
          setAudioRecordingStatus,
          microphoneEnabled,
          aiVoiceCardState,
          learnerVoiceCardState,
          aiMediaStream,
          learnerMediaStream,
          voiceRecordingState,
          isStreamError,
          handleRestartFromStreamError,
          inactivityCount,
        }}
      >
        <div css={wrapperStyles}>
          <ConversationModalContent />
        </div>
      </AiConversationContext.Provider>
    </>
  );
};

export default NewConversationView;
